import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Target, TrendingUp, Wallet } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../../../components/ui/table';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const R = (n) => `R ${(Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  planning:    'bg-slate-600 text-slate-100',
  in_progress: 'bg-blue-600 text-white',
  completed:   'bg-emerald-600 text-white',
  on_hold:     'bg-amber-600 text-white',
  cancelled:   'bg-rose-700 text-white',
};
const STATUS_OPTIONS = ['planning', 'in_progress', 'completed', 'on_hold', 'cancelled'];

const emptyForm = { name: '', planned_cost: '', spent_so_far: '', target_date: '', status: 'planning', notes: '' };

export default function ExpansionBudgetPanel() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState({ total_projects: 0, total_planned: 0, total_spent: 0, remaining: 0 });
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/expansion-budget/projects`, { headers });
      setProjects(res.data.projects || []);
      setSummary(res.data.summary || {});
    } catch { toast.error('Failed to load expansion budget'); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      planned_cost: p.planned_cost ?? '',
      spent_so_far: p.spent_so_far ?? '',
      target_date: p.target_date || '',
      status: p.status || 'planning',
      notes: p.notes || '',
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Project name required'); return; }
    if (form.planned_cost === '' || isNaN(parseFloat(form.planned_cost))) { toast.error('Valid planned cost required'); return; }
    const payload = {
      name: form.name.trim(),
      planned_cost: parseFloat(form.planned_cost),
      spent_so_far: parseFloat(form.spent_so_far) || 0,
      target_date: form.target_date || null,
      status: form.status,
      notes: form.notes.trim() || null,
    };
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/expansion-budget/projects/${editing.id}`, payload, { headers });
        toast.success('Project updated');
      } else {
        await axios.post(`${API_URL}/api/expansion-budget/projects`, payload, { headers });
        toast.success('Project added');
      }
      setShowDialog(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/expansion-budget/projects/${p.id}`, { headers });
      toast.success('Project deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const progressPct = (p) => {
    const pc = Number(p.planned_cost) || 0;
    if (pc <= 0) return 0;
    return Math.min(100, Math.round(((Number(p.spent_so_far) || 0) / pc) * 100));
  };

  return (
    <div className="space-y-4" data-testid="expansion-budget-panel">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0 text-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><Target className="h-4 w-4" /><span className="text-xs opacity-80">Projects</span></div>
            <p className="text-2xl font-bold" data-testid="eb-summary-count">{summary.total_projects || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-600 to-amber-700 border-0 text-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><TrendingUp className="h-4 w-4" /><span className="text-xs opacity-80">Total Planned</span></div>
            <p className="text-xl font-bold">{R(summary.total_planned)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-rose-600 to-rose-700 border-0 text-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><Wallet className="h-4 w-4" /><span className="text-xs opacity-80">Spent So Far</span></div>
            <p className="text-xl font-bold">{R(summary.total_spent)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-0 text-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><Wallet className="h-4 w-4" /><span className="text-xs opacity-80">Remaining</span></div>
            <p className="text-xl font-bold">{R(summary.remaining)}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-slate-900 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
          <CardTitle className="text-base text-white">Planned Expansion Projects</CardTitle>
          <Button size="sm" onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="new-project-btn">
            <Plus className="h-4 w-4 mr-1" /> New Project
          </Button>
        </CardHeader>
        <CardContent>
          {projects.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              No expansion projects yet. Click <b>New Project</b> to start planning — e.g. opening a new branch, buying a vehicle, equipment upgrade.
            </div>
          ) : (
            <div className="rounded-lg border border-slate-700 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 bg-slate-800/60">
                    <TableHead className="text-slate-400">Project</TableHead>
                    <TableHead className="text-slate-400 text-right">Planned</TableHead>
                    <TableHead className="text-slate-400 text-right">Spent</TableHead>
                    <TableHead className="text-slate-400 text-right">Remaining</TableHead>
                    <TableHead className="text-slate-400">Progress</TableHead>
                    <TableHead className="text-slate-400">Target</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map(p => {
                    const remaining = (Number(p.planned_cost) || 0) - (Number(p.spent_so_far) || 0);
                    const pct = progressPct(p);
                    return (
                      <TableRow key={p.id} className="border-slate-700">
                        <TableCell className="text-white">
                          <div className="font-semibold">{p.name}</div>
                          {p.notes && <div className="text-xs text-slate-500 truncate max-w-xs">{p.notes}</div>}
                        </TableCell>
                        <TableCell className="text-right text-amber-400 font-semibold">{R(p.planned_cost)}</TableCell>
                        <TableCell className="text-right text-rose-400">{R(p.spent_so_far)}</TableCell>
                        <TableCell className={`text-right font-semibold ${remaining >= 0 ? 'text-emerald-400' : 'text-rose-500'}`}>{R(remaining)}</TableCell>
                        <TableCell className="min-w-[140px]">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-800 rounded-full h-2 overflow-hidden border border-slate-700">
                              <div className={`h-full ${pct > 90 ? 'bg-rose-500' : pct > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-slate-300 w-9 text-right">{pct}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-300 text-xs">{p.target_date || '—'}</TableCell>
                        <TableCell>
                          <Badge className={STATUS_STYLES[p.status] || 'bg-slate-700'}>{(p.status || '').replace('_', ' ').toUpperCase()}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" className="h-7 w-7 p-0 bg-blue-600 hover:bg-blue-700" onClick={() => openEdit(p)} data-testid={`edit-project-${p.id}`}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button size="sm" className="h-7 w-7 p-0 bg-rose-600 hover:bg-rose-700" onClick={() => remove(p)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Expansion Project' : 'New Expansion Project'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-300 text-xs">Project Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Open second branch in Joburg"
                className="bg-slate-800 border-slate-700"
                data-testid="project-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-xs">Planned Cost (R) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.planned_cost}
                  onChange={(e) => setForm({ ...form, planned_cost: e.target.value })}
                  placeholder="250000.00"
                  className="bg-slate-800 border-slate-700"
                  data-testid="project-planned-input"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Spent So Far (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.spent_so_far}
                  onChange={(e) => setForm({ ...form, spent_so_far: e.target.value })}
                  placeholder="0"
                  className="bg-slate-800 border-slate-700"
                  data-testid="project-spent-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-xs">Target Date</Label>
                <Input
                  type="date"
                  value={form.target_date}
                  onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="bg-slate-800 border-slate-700" placeholder="Scope, milestones, dependencies..." />
            </div>
            <Button onClick={save} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="save-project-btn">Save Project</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
